import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';
import vegetationVert from '../../glsl/vegetation_vert.glsl';
import vegetationFrag from '../../glsl/vegetation_frag.glsl';
import { TERRAIN, VEGETATION, RENDERING } from '../core/SimConfig.js';
import VegetationSplatMap, { CHANNEL_OFFSET } from './VegetationSplatMap.js';
import { lerp } from '../utils.js';

/**
 * Procedural vegetation scattering.
 *
 *   - World XZ is divided into a fixed grid of `cellSize` cells. The cells
 *     within `viewDistance` of the aircraft form the active set.
 *   - Each active cell deterministically generates `candidatesPerCell`
 *     candidates (jittered grid). A candidate becomes a live billboard if
 *     splat density at its position exceeds a per-slot random threshold.
 *   - One InstancedBufferGeometry + RawShaderMaterial per asset. Yaw-aligned
 *     single quads (face camera horizontally, vertical axis locked to +Y).
 *   - Cells whose terrain heightmap hasn't streamed in yet are marked
 *     deferred and retried each frame until populated.
 *
 *  Slot allocation per asset is via a stack-style free list. Dead slots get
 *  iScale = 0, collapsing the quad to a point in the vertex shader (no
 *  fragments emitted). instanceCount stays at maxInstances — the extra vertex
 *  work for dead slots is negligible at the scales we hit.
 */

// No persistent scratch state — cell activations are infrequent enough that
// per-call allocations don't matter, and reusing a module-level array would
// be unsafe if activation ever became reentrant.

export default class VegetationSystem {
  /**
   * @param {{
   *   scene: THREE.Scene,
   *   terrainCollider: import('./TerrainCollider.js').default,
   *   anisotropy?: number,
   *   basePath?: string,
   * }} opts
   */
  constructor({ scene, terrainCollider, anisotropy = 1, basePath = '' }) {
    this.scene = scene;
    this.terrainCollider = terrainCollider;
    this.anisotropy = anisotropy;
    this.basePath = basePath;

    this.group = new THREE.Group();
    this.group.name = 'Vegetation';
    scene.add(this.group);

    /** @type {Array<AssetPool>} */
    this._assets = [];
    /** @type {Map<string, CellState>} */
    this._activeCells = new Map();
    /** @type {Set<string>} */
    this._deferredCells = new Set();
    this._lastCenterCellX = NaN;
    this._lastCenterCellZ = NaN;

    // Terrain grid bounds (used for in-grid candidate filtering)
    const { cols, rows, tileSize, origin } = TERRAIN;
    const [ox, oz] = origin;
    this._gridMinX = ox - (cols * tileSize) / 2;
    this._gridMinZ = oz - (rows * tileSize) / 2;
    this._gridMaxX = this._gridMinX + cols * tileSize;
    this._gridMaxZ = this._gridMinZ + rows * tileSize;

    this._splat = new VegetationSplatMap();
    this._tint = new THREE.Color(0xffffff);
    this._logDepthBufFC = 2.0 / (Math.log(RENDERING.farPlane + 1.0) / Math.LN2);

    // Seeded 2D noise field for large-scale density/size variation. Shared
    // across all assets so patches correlate (lush areas grow all species
    // denser and larger, sparse areas thin them all out together).
    this._noise2D = createNoise2D(mulberry32(VEGETATION.noise.seed | 0));

    this.ready = false;
    this._loadAssets();
  }

  /**
   * Async load splat map + per-asset textures. Each asset failing is non-fatal
   * (it's just skipped). The system becomes `ready` once the splat and at
   * least one asset texture have loaded.
   */
  async _loadAssets() {
    const loader = new THREE.TextureLoader();
    const splatPath = `${this.basePath}assets/${VEGETATION.splatMap}`;

    const splatOk = await this._splat.load(splatPath);
    if (!splatOk) {
      console.warn(`VegetationSystem: splat map missing at ${splatPath} — system disabled.`);
      return;
    }

    const assetPromises = VEGETATION.assets.map((cfg) => new Promise((resolve) => {
      const url = `${this.basePath}assets/${cfg.texture}`;
      loader.load(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.anisotropy = this.anisotropy;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.magFilter = THREE.LinearFilter;
          resolve({ cfg, tex });
        },
        undefined,
        () => {
          console.warn(`VegetationSystem: failed to load ${url} — asset '${cfg.name}' skipped.`);
          resolve(null);
        },
      );
    }));

    const loaded = (await Promise.all(assetPromises)).filter(Boolean);
    if (loaded.length === 0) {
      console.warn('VegetationSystem: no asset textures loaded — system disabled.');
      return;
    }

    for (const { cfg, tex } of loaded) {
      this._assets.push(this._buildAssetPool(cfg, tex));
    }
    this.ready = true;
  }

  _buildAssetPool(cfg, texture) {
    const N = cfg.maxInstances;

    // Quad geometry: base on ground (y = 0), height 1, width 1, centred in X.
    // Per-instance attributes scale it to world units.
    const geometry = new THREE.InstancedBufferGeometry();
    const positions = new Float32Array([
      -0.5, 0, 0,   // 0: bottom-left
       0.5, 0, 0,   // 1: bottom-right
      -0.5, 1, 0,   // 2: top-left
       0.5, 1, 0,   // 3: top-right
    ]);
    const uvs = new Float32Array([
      0, 0,  // bottom-left
      1, 0,  // bottom-right
      0, 1,  // top-left
      1, 1,  // top-right
    ]);
    const indices = new Uint16Array([0, 1, 2, 2, 1, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv',       new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    const iPositionArr = new Float32Array(N * 3);
    const iScaleSeedArr = new Float32Array(N * 2);
    // Default to scale = 0 so unused slots are invisible.
    // (Float32Array is already zero-initialised; left explicit for clarity.)

    const iPositionAttr  = new THREE.InstancedBufferAttribute(iPositionArr, 3);
    const iScaleSeedAttr = new THREE.InstancedBufferAttribute(iScaleSeedArr, 2);
    iPositionAttr.setUsage(THREE.DynamicDrawUsage);
    iScaleSeedAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('iPosition',  iPositionAttr);
    geometry.setAttribute('iScaleSeed', iScaleSeedAttr);
    geometry.instanceCount = N;

    const material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: vegetationVert,
      fragmentShader: vegetationFrag,
      uniforms: {
        uMap:            { value: texture },
        uAlphaTest:      { value: VEGETATION.alphaTest },
        uOpacity:        { value: VEGETATION.opacity },
        uTint:           { value: this._tint },
        uCameraPos:      { value: new THREE.Vector3() },
        uFadeStart:      { value: VEGETATION.fadeStart },
        uFadeEnd:        { value: VEGETATION.fadeEnd },
        uLogDepthBufFC:  { value: this._logDepthBufFC },
      },
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
    });

    // Bounding sphere is global (sliding window keeps everything within
    // viewDistance of the aircraft anyway). frustumCulled = false skips the
    // built-in cull pass — we manage culling at the cell level.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = `Vegetation_${cfg.name}`;
    this.group.add(mesh);

    // Free list: stack of available slot indices. freeList[freeCount-1] is
    // the top (last allocated comes back first).
    const freeList = new Uint32Array(N);
    for (let i = 0; i < N; i++) freeList[i] = N - 1 - i;

    return {
      cfg,
      texture,
      material,
      geometry,
      mesh,
      iPositionArr,
      iScaleSeedArr,
      iPositionAttr,
      iScaleSeedAttr,
      dirty: false,
      freeList,
      freeCount: N,
      channelOffset: CHANNEL_OFFSET[cfg.channel] ?? 0,
    };
  }

  // ── Public ───────────────────────────────────────────────────────────

  /**
   * @param {{ top:[number,number,number], bottom:[number,number,number] } | undefined} skyTint
   * @param {number} [effectBrightness=1] — per-scenario dim; vegetation is never
   *   emissive, so it folds straight into the tint (matches the env-lit dim
   *   applied to particles/smoke).
   */
  setLighting(skyTint, effectBrightness = 1) {
    // Cheap proxy for ambient/skylight: bias the tint toward the hemisphere's
    // upper half (matches ParticleSystem's choice). Reinhard-soft so bright
    // HDRs don't blow vegetation pure white.
    if (!skyTint) {
      this._tint.setRGB(effectBrightness, effectBrightness, effectBrightness);
      return;
    }
    const { top, bottom } = skyTint;
    const W = 0.7; // sky-up weight; matches particle shader's SKY_UP_WEIGHT
    let r = bottom[0] * (1 - W) + top[0] * W;
    let g = bottom[1] * (1 - W) + top[1] * W;
    let b = bottom[2] * (1 - W) + top[2] * W;
    r = r / (1 + r) * 2 * effectBrightness;
    g = g / (1 + g) * 2 * effectBrightness;
    b = b / (1 + b) * 2 * effectBrightness;
    this._tint.setRGB(r, g, b);
  }

  update(camera) {
    if (!this.ready) return;
    if (!camera) return;

    const camPos = camera.position;

    // Update camera uniform on every asset material — single Vector3 shared
    // across all of them via assignment below.
    for (const a of this._assets) {
      a.material.uniforms.uCameraPos.value.copy(camPos);
    }

    const cellSize = VEGETATION.cellSize;
    const cx = Math.floor((camPos.x - this._gridMinX) / cellSize);
    const cz = Math.floor((camPos.z - this._gridMinZ) / cellSize);

    // Recompute active set only when the camera crosses a cell boundary. Also
    // retry any deferred cells each frame regardless (heightmap may have
    // streamed in).
    if (cx !== this._lastCenterCellX || cz !== this._lastCenterCellZ) {
      this._lastCenterCellX = cx;
      this._lastCenterCellZ = cz;
      this._rebuildActiveSet(camPos);
    }
    if (this._deferredCells.size > 0) {
      this._retryDeferred(camPos);
    }

    // Push any attribute changes to the GPU.
    for (const a of this._assets) {
      if (a.dirty) {
        a.iPositionAttr.needsUpdate = true;
        a.iScaleSeedAttr.needsUpdate = true;
        a.dirty = false;
      }
    }
  }

  dispose() {
    for (const a of this._assets) {
      this.group.remove(a.mesh);
      a.geometry.dispose();
      a.material.dispose();
      a.texture.dispose();
    }
    this._assets.length = 0;
    this._activeCells.clear();
    this._deferredCells.clear();
    this._splat.dispose();
    this.scene.remove(this.group);
  }

  // ── Cell management ─────────────────────────────────────────────────

  _rebuildActiveSet(centerPos) {
    const cellSize = VEGETATION.cellSize;
    const radius = VEGETATION.viewDistance + cellSize * 0.5;
    const radius2 = radius * radius;
    const radiusCells = Math.ceil(VEGETATION.viewDistance / cellSize) + 1;

    const cx0 = this._lastCenterCellX;
    const cz0 = this._lastCenterCellZ;

    // Collect new active set
    const newActive = new Set();
    for (let dz = -radiusCells; dz <= radiusCells; dz++) {
      for (let dx = -radiusCells; dx <= radiusCells; dx++) {
        const cx = cx0 + dx;
        const cz = cz0 + dz;
        const centerX = this._gridMinX + (cx + 0.5) * cellSize;
        const centerZ = this._gridMinZ + (cz + 0.5) * cellSize;
        const ddx = centerX - centerPos.x;
        const ddz = centerZ - centerPos.z;
        if (ddx * ddx + ddz * ddz > radius2) continue;
        newActive.add(this._cellKey(cx, cz));
      }
    }

    // Deactivate cells that left the set
    for (const key of this._activeCells.keys()) {
      if (!newActive.has(key)) {
        this._deactivateCell(key);
      }
    }

    // Activate new cells
    for (const key of newActive) {
      if (!this._activeCells.has(key)) {
        this._tryActivateCell(key);
      }
    }
  }

  _retryDeferred(_centerPos) {
    // Snapshot keys — _tryActivateCell mutates _deferredCells.
    const keys = [...this._deferredCells];
    for (const key of keys) {
      // Cell might have moved out of range since being deferred — check it's
      // still in _activeCells (set to a placeholder during defer).
      if (!this._activeCells.has(key)) {
        this._deferredCells.delete(key);
        continue;
      }
      this._tryActivateCell(key, /* isRetry */ true);
    }
  }

  _tryActivateCell(key, isRetry = false) {
    const { cx, cz } = this._parseCellKey(key);
    const cellSize = VEGETATION.cellSize;
    const cellMinX = this._gridMinX + cx * cellSize;
    const cellMinZ = this._gridMinZ + cz * cellSize;
    const slotsPerSide = Math.round(Math.sqrt(VEGETATION.candidatesPerCell));
    const slotsTotal = slotsPerSide * slotsPerSide;
    const sub = cellSize / slotsPerSide;

    // Per-cell noise: sample the field at the 4 cell corners once, bilerp
    // per-candidate. The noise wavelength (~hundreds of m) is much larger
    // than cellSize, so 4 corner samples reconstruct the field smoothly
    // across the cell with no visible cell-boundary discontinuities — and
    // the inner loop drops from a noise call per candidate to a few muls.
    const noiseCfg = VEGETATION.noise;
    const inv = 1 / noiseCfg.scale;
    const cellMaxX = cellMinX + cellSize;
    const cellMaxZ = cellMinZ + cellSize;
    const n00 = this._noise2D(cellMinX * inv, cellMinZ * inv) * 0.5 + 0.5;
    const n10 = this._noise2D(cellMaxX * inv, cellMinZ * inv) * 0.5 + 0.5;
    const n01 = this._noise2D(cellMinX * inv, cellMaxZ * inv) * 0.5 + 0.5;
    const n11 = this._noise2D(cellMaxX * inv, cellMaxZ * inv) * 0.5 + 0.5;
    const invCellSize = 1 / cellSize;

    // Build per-asset candidate lists. If any required terrain height is
    // missing, mark the cell as deferred and bail before mutating pools.
    const perAsset = new Array(this._assets.length);
    for (let ai = 0; ai < this._assets.length; ai++) {
      perAsset[ai] = [];
      const a = this._assets[ai];
      const cfg = a.cfg;
      const seed = cfg.seed | 0;

      for (let sIdx = 0; sIdx < slotsTotal; sIdx++) {
        const sx = sIdx % slotsPerSide;
        const sy = (sIdx - sx) / slotsPerSide;

        // Four independent random samples per candidate, all derived from
        // (cellX, cellZ, slotIdx, assetSeed) so placement is deterministic.
        const h0 = hash01(cx, cz, sIdx, seed);
        const h1 = hash01(cx, cz, sIdx, seed ^ 0x9e3779b9);
        const h2 = hash01(cx, cz, sIdx, seed ^ 0x85ebca6b);
        const h3 = hash01(cx, cz, sIdx, seed ^ 0xc2b2ae35);

        const px = cellMinX + (sx + h0) * sub;
        const pz = cellMinZ + (sy + h1) * sub;

        // Skip out-of-grid candidates entirely (no terrain to stand on).
        if (px < this._gridMinX || px >= this._gridMaxX ||
            pz < this._gridMinZ || pz >= this._gridMaxZ) continue;

        // Bilinear-interpolate the cell-corner noise to this candidate.
        // High noise → dense + tall; low noise → sparse + small.
        const u = (px - cellMinX) * invCellSize;
        const v = (pz - cellMinZ) * invCellSize;
        const n = (n00 * (1 - u) + n10 * u) * (1 - v)
                + (n01 * (1 - u) + n11 * u) * v;
        const densityMul = lerp(noiseCfg.densityLow, noiseCfg.densityHigh, n);
        const sizeMul    = lerp(noiseCfg.sizeLow,    noiseCfg.sizeHigh,    n);

        // Density check.
        let d = this._splat.sample(px, pz, a.channelOffset) * cfg.densityScale * densityMul;
        if (cfg.densityGamma !== 1) d = Math.pow(d, cfg.densityGamma);
        if (d < cfg.threshold) continue;
        if (h2 >= d) continue;

        // Size + per-instance seed.
        const size = lerp(cfg.sizeMin, cfg.sizeMax, h3) * sizeMul;
        perAsset[ai].push({ px, pz, size, seed: h0 });
      }
    }

    // Batch terrain height query per asset. If anything comes back NaN, the
    // tile heightmap isn't streamed yet — defer the entire cell so it can be
    // retried without leaving partial state behind.
    const heightsPerAsset = new Array(this._assets.length);
    for (let ai = 0; ai < this._assets.length; ai++) {
      const list = perAsset[ai];
      if (list.length === 0) { heightsPerAsset[ai] = null; continue; }

      const positions = new Array(list.length);
      for (let i = 0; i < list.length; i++) {
        positions[i] = { x: list[i].px, z: list[i].pz };
      }
      const heights = this.terrainCollider.getHeightsAt(positions);
      for (let i = 0; i < heights.length; i++) {
        if (Number.isNaN(heights[i])) {
          // Defer the whole cell.
          if (!isRetry) {
            this._activeCells.set(key, { slotsByAsset: null, deferred: true });
          }
          this._deferredCells.add(key);
          return;
        }
      }
      heightsPerAsset[ai] = heights;
    }

    // Commit: allocate slots and write instance attributes.
    const slotsByAsset = new Array(this._assets.length);
    for (let ai = 0; ai < this._assets.length; ai++) {
      const a = this._assets[ai];
      const list = perAsset[ai];
      const heights = heightsPerAsset[ai];
      const allocated = [];
      slotsByAsset[ai] = allocated;
      if (!list || list.length === 0) continue;

      for (let i = 0; i < list.length; i++) {
        if (a.freeCount === 0) break; // pool exhausted — drop remaining
        const slot = a.freeList[--a.freeCount];
        const c = list[i];
        const base3 = slot * 3;
        const base2 = slot * 2;
        a.iPositionArr[base3 + 0] = c.px;
        a.iPositionArr[base3 + 1] = heights[i] + a.cfg.yOffset;
        a.iPositionArr[base3 + 2] = c.pz;
        a.iScaleSeedArr[base2 + 0] = c.size;
        a.iScaleSeedArr[base2 + 1] = c.seed;
        allocated.push(slot);
      }
      a.dirty = true;
    }

    this._activeCells.set(key, { slotsByAsset, deferred: false });
    this._deferredCells.delete(key);
  }

  _deactivateCell(key) {
    const state = this._activeCells.get(key);
    this._activeCells.delete(key);
    this._deferredCells.delete(key);
    if (!state || state.deferred || !state.slotsByAsset) return;

    for (let ai = 0; ai < this._assets.length; ai++) {
      const a = this._assets[ai];
      const slots = state.slotsByAsset[ai];
      if (!slots || slots.length === 0) continue;
      for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        // Mark slot dead — vertex shader collapses iScale=0 to a point.
        a.iScaleSeedArr[slot * 2 + 0] = 0;
        a.freeList[a.freeCount++] = slot;
      }
      a.dirty = true;
    }
  }

  _cellKey(cx, cz) {
    // String keys — Map handles them efficiently and we sidestep the
    // 32-bit-overflow trap that bit-packing would hit at reasonable cell
    // counts (cz << 17 wraps when (cx + offset) is non-trivial).
    return `${cx},${cz}`;
  }

  _parseCellKey(key) {
    const comma = key.indexOf(',');
    return {
      cx: parseInt(key.substring(0, comma), 10),
      cz: parseInt(key.substring(comma + 1), 10),
    };
  }
}

// ── Hashing ─────────────────────────────────────────────────────────────────

/**
 * Deterministic 32-bit integer hash → float in [0, 1).
 * xxHash-flavoured: prime-multiply each input, XOR fold, mix-shift, normalise.
 * Stable across sessions; same inputs always produce the same output.
 */
function hash01(x, y, z, seed) {
  let h = Math.imul(x | 0, 0x16519e63) | 0;
  h = (h + Math.imul(y | 0, 0x27d4eb2f)) | 0;
  h = h ^ Math.imul(z | 0, 0x165667b1);
  h = (h ^ (seed | 0)) | 0;
  h = Math.imul(h ^ (h >>> 13), 0x4cf5ad43) | 0;
  h = h ^ (h >>> 16);
  return (h >>> 0) / 0x100000000;
}

// Mulberry32 — small seeded PRNG used to make the simplex field reproducible
// across sessions. simplex-noise calls this once per gradient init.
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

/**
 * @typedef {object} AssetPool
 * @property {object} cfg
 * @property {THREE.Texture} texture
 * @property {THREE.RawShaderMaterial} material
 * @property {THREE.InstancedBufferGeometry} geometry
 * @property {THREE.Mesh} mesh
 * @property {Float32Array} iPositionArr
 * @property {Float32Array} iScaleSeedArr
 * @property {THREE.InstancedBufferAttribute} iPositionAttr
 * @property {THREE.InstancedBufferAttribute} iScaleSeedAttr
 * @property {boolean} dirty
 * @property {Uint32Array} freeList
 * @property {number} freeCount
 * @property {number} channelOffset
 */

/**
 * @typedef {object} CellState
 * @property {Array<Array<number>>|null} slotsByAsset
 * @property {boolean} deferred
 */
